import { animate, style, transition, trigger } from '@angular/animations';
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { QuestionModel } from 'src/app/api/models';
import { QuestionType } from 'src/app/app.types';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { NgScrollbar } from 'ngx-scrollbar';
import { NgTemplateOutlet, DecimalPipe, NgClass } from '@angular/common';
import { MockPhoneComponent } from '../common/mock-phone.component';
import { MatIcon } from '@angular/material/icon';
import { MarkdownKatexComponent } from '../../widget/markdown-katex/markdown-katex.component';
import { MatButton, MatAnchor } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { EnvPipe } from 'src/app/utility/env.pipe';
import { shuffleAnswers } from 'src/app/utility/app.utils';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'qa-question-mock-phone',
  templateUrl: './question-mock-phone.component.html',
  styleUrls: ['./question-mock-phone.component.scss'],
  animations: [
    trigger('flyInOutVertical', [
      transition(':enter', [
        style({ bottom: '-150px' }),
        animate('200ms ease-out', style({ bottom: '44px' })),
      ]),
      transition(':leave', [
        animate('200ms ease-out', style({ bottom: '-150px' })),
      ]),
    ]),
    trigger('flyInOutHorizontal', [
      transition(':enter', [
        style({ right: '-248px' }),
        animate('250ms ease-out', style({ right: '0px' })),
      ]),
      transition(':leave', [
        animate('250ms ease-out', style({ right: '-248px' })),
      ]),
    ]),
  ],
  imports: [
    MockPhoneComponent,
    NgScrollbar,
    MatIcon,
    MarkdownKatexComponent,
    NgTemplateOutlet,
    NgClass,
    MatButton,
    MatAnchor,
    MatTooltip,
    DecimalPipe,
    EnvPipe,
    TranslateModule,
    MatCheckboxModule,
  ],
})
export class QuestionMockPhoneComponent implements OnChanges {
  constructor(
    private translator: TranslateService,
    private domSanitizer: DomSanitizer
  ) {}
  @Input() question: QuestionModel;
  @Input() itemCount: number;
  @Input() isExam: boolean;
  @Input() isPollQuestion: boolean;
  @Input() currentItemPosition = 1;
  @Input() brandingColor: string;
  @Input() displayAnswersInOrder: boolean;
  isAnswerContainerExpanded = false;
  isExplanationTooltipVisible = false;
  isTextContainerExpanded = false;
  isWeblinkTabActive = false;
  questionTypeEnum = QuestionType;
  showExplanation = false;
  isExplanationPanelOpen = false;
  @ViewChild(NgScrollbar) scrollbarRef: NgScrollbar;

  get trueFalseAnswerTrue() {
    return {
      is_right: this.question.is_right,
      text: this.translator.instant('questionMockPhone.correct'),
    };
  }
  get trueFalseAnswerFalse() {
    return {
      is_right: !this.question.is_right,
      text: this.translator.instant('questionMockPhone.inCorrect'),
    };
  }
  get maxAnswerSubmissions(): number[] {
    const length =
      this.question?.max_answer_submissions < 11
        ? this.question?.max_answer_submissions
        : 10;
    return Array.from({ length }, (_, i) => i + 1);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.displayAnswersInOrder) {
      this.question.display_in_order =
        changes.displayAnswersInOrder.currentValue;
      if (this.question.answers.length > 1 && !this.question.display_in_order) {
        this.question.answers = shuffleAnswers(this.question.answers);
      }
    }
    this.currentItemPosition =
      changes?.currentItemPosition?.currentValue || null;
    setTimeout(() => {
      if (this.scrollbarRef) {
        this.scrollbarRef.update();
      }
    }, 0);
    if (changes.brandingColor && changes.brandingColor.currentValue) {
      this.brandingColor = changes.brandingColor.currentValue;
    }
  }

  getQuestionTypeText(questionType: QuestionType) {
    switch (questionType) {
      case QuestionType.MULTIPLE_CHOICE:
        return this.translator.instant('questionMockPhone.multiChoice');
      case QuestionType.SINGLE_CHOICE:
        return this.translator.instant('questionMockPhone.singleChoice');
      case QuestionType.TRUE_FALSE:
        return this.translator.instant('questionMockPhone.trueOrFalse');
      case QuestionType.FREETEXT:
        return this.translator.instant('questionMockPhone.freeText');
      default:
        return '';
    }
  }

  openExplainPanel(event, open): void {
    if (open) {
      event.stopPropagation();
      this.isExplanationPanelOpen = !this.isExplanationPanelOpen;
    }
  }

  getWeblink(): SafeUrl {
    let weblink = this.question.weblink;
    if (!weblink) {
      return null;
    }
    if (!weblink.includes('http')) {
      weblink = `http://${weblink}`;
    }
    return this.domSanitizer.bypassSecurityTrustUrl(weblink);
  }
}
